import { useState, useEffect, useRef, useCallback } from "react"

// ─── helpers ────────────────────────────────────────────────────────────────

export function htmlToText(html) {
  if (!html) return ""
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/_/g, " ")
    .replace(/\[(\d+)Answer char=\d+\]/g, "")
    .replace(/\[Answer char=\d+\]/g, "")
    .replace(/\s+/g, " ")
    .trim()
}


// export function isYearOneToThree(level) {
//     const lowerLevel = level?.toLowerCase();

//     if (!lowerLevel) {
//         return false;
//     }

//     const primaryMatch = lowerLevel.match(/primary\s*(\d+)/);
//     if (primaryMatch) {
//         const year = parseInt(primaryMatch[1], 10);
//         return year >= 1 && year <= 3;
//     }

//     const yearMatch = lowerLevel.match(/year\s*(\d+)/);
//     if (yearMatch) {
//         const year = parseInt(yearMatch[1], 10);
//         return year >= 1 && year <= 3;
//     }

//     return false;
// }

export function isYearOneToThree(level) {
    const lowerLevel = level?.toLowerCase();
    if (!lowerLevel) return false;

    const matches = [...lowerLevel.matchAll(/(?:primary|year)\s*(\d+)/g)];
    if (!matches.length) return false;

    return matches.every(m => {
        const year = parseInt(m[1], 10);
        return year >= 1 && year <= 3;
    });
}

function cancelSpeech() {
  if (window.speechSynthesis) window.speechSynthesis.cancel()
}

// Split long text into sentence-sized chunks so Chrome doesn't silently
// die partway through a long utterance (Chromium bug w/ network voices).
function splitIntoChunks(text) {
  const chunks = text.match(/[^.!?]+[.!?]*/g) || [text]
  return chunks.map(c => c.trim()).filter(Boolean)
}

const SPEED_OPTIONS = [
  { label: "0.5x", rate: 0.5 },
  { label: "1x",   rate: 1   },
  { label: "1.5x", rate: 1.5 },
  { label: "2x",   rate: 2   },
]
const DEFAULT_SPEED_INDEX = 1

// Preferred UK English voice names, in priority order. Different browsers/OS
// ship different voice names, so we try a list rather than one exact match.
const UK_VOICE_PREFERENCES = [
  "Google UK English Female",
  "Google UK English Male",
  "Microsoft Libby Online (Natural) - English (United Kingdom)",
  "Microsoft Sonia Online (Natural) - English (United Kingdom)",
  "Microsoft Ryan Online (Natural) - English (United Kingdom)",
  "Microsoft Hazel - English (Great Britain)",
  "Daniel",          // Safari/macOS UK male voice
  "Kate",            // Safari/macOS UK female voice
  "Serena",          // Safari/macOS UK female voice
]

// ─── voice loading (async + cross-browser safe) ────────────────────────────

function getVoicesAsync() {
  return new Promise(resolve => {
    if (!window.speechSynthesis) return resolve([])
    let voices = window.speechSynthesis.getVoices()
    if (voices.length) return resolve(voices)

    // Chrome/Edge/Firefox populate voices asynchronously on first load
    const onVoicesChanged = () => {
      voices = window.speechSynthesis.getVoices()
      if (voices.length) {
        window.speechSynthesis.removeEventListener("voiceschanged", onVoicesChanged)
        resolve(voices)
      }
    }
    window.speechSynthesis.addEventListener("voiceschanged", onVoicesChanged)

    // Safari sometimes never fires voiceschanged reliably — poll as a fallback
    let attempts = 0
    const poll = setInterval(() => {
      attempts++
      voices = window.speechSynthesis.getVoices()
      if (voices.length || attempts > 20) {
        clearInterval(poll)
        window.speechSynthesis.removeEventListener("voiceschanged", onVoicesChanged)
        resolve(voices)
      }
    }, 250)
  })
}

function pickUkVoice(voices) {
  if (!voices || !voices.length) return null

  // 1. Exact name match against our preference list
  for (const name of UK_VOICE_PREFERENCES) {
    const match = voices.find(v => v.name === name)
    if (match) return match
  }

  // 2. Any voice whose lang is en-GB
  const enGb = voices.filter(v => /en[-_]GB/i.test(v.lang))
  if (enGb.length) {
    // Prefer ones that look "Online"/"Natural"/Google-branded, else just the first
    const preferred = enGb.find(v => /online|natural|google/i.test(v.name))
    return preferred || enGb[0]
  }

  // 3. Fall back to any voice with "UK" or "British" in the name
  const nameMatch = voices.find(v => /uk|british/i.test(v.name))
  if (nameMatch) return nameMatch

  return null
}

// ─── hook ───────────────────────────────────────────────────────────────────

/**
 * useSpeaker()
 * Returns { speak, stop, activeId, speedIndex, setSpeedIndex, speedOptions, voiceReady, voiceName }
 */
export function useSpeaker() {
  const [activeId, setActiveId] = useState(null)
  const [speedIndex, setSpeedIndexRaw] = useState(DEFAULT_SPEED_INDEX)
  const [voiceReady, setVoiceReady] = useState(false)
  const [voiceName, setVoiceName] = useState(null)

  const activeIdRef   = useRef(null)
  const speedRef       = useRef(SPEED_OPTIONS[DEFAULT_SPEED_INDEX].rate)
  const activeTextRef  = useRef("")
  const voiceRef       = useRef(null)
  const cancelChunkRef = useRef(null) // lets stop() interrupt a chunked read
  const keepAliveRef   = useRef(null)

  activeIdRef.current = activeId

  useEffect(() => {
    let mounted = true
    getVoicesAsync().then(voices => {
      if (!mounted) return
      const uk = pickUkVoice(voices)
      voiceRef.current = uk
      setVoiceName(uk ? uk.name : null)
      setVoiceReady(true)
    })
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    return () => {
      cancelSpeech()
      if (keepAliveRef.current) clearInterval(keepAliveRef.current)
    }
  }, [])

  const stop = useCallback(() => {
    if (typeof cancelChunkRef.current === "function") {
      cancelChunkRef.current() // flips myCancelToken.cancelled = true for the in-flight chunk loop
    }
    cancelSpeech()
    if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null }
    setActiveId(null)
  }, [])

  // Speaks `text` in sentence-sized chunks, sequentially, using the UK voice.
  // Includes: watchdog for Chrome silent-fail, keep-alive resume() for the
  // 15s-pause bug, and a cancel flag so stop()/toggle-off interrupts cleanly.
  const _startUtterance = useCallback((id, text) => {
    // Kill any previous chunk loop first — otherwise its cancel flag is lost
    //   // (overwritten below) and it keeps calling speak() forever, racing the
    // new loop even after cancelSpeech()/onerror fires on it.
    if (typeof cancelChunkRef.current === "function") {
      cancelChunkRef.current()
    }

    cancelSpeech()
    if (keepAliveRef.current) clearInterval(keepAliveRef.current)

    const myCancelToken = { cancelled: false }
    cancelChunkRef.current = () => { myCancelToken.cancelled = true }

    const chunks = splitIntoChunks(text)
    let idx = 0
    let firedStart = false

    // Chrome pauses speechSynthesis after ~15s idle — nudge it awake periodically
    keepAliveRef.current = setInterval(() => {
      if (window.speechSynthesis.speaking && window.speechSynthesis.paused) {
        window.speechSynthesis.resume()
      }
    }, 10000)

    const speakNext = () => {
      if (myCancelToken.cancelled) return
      if (idx >= chunks.length) {
        clearInterval(keepAliveRef.current)
        keepAliveRef.current = null
        setActiveId(prev => (prev === id ? null : prev))
        return
      }

      const u = new SpeechSynthesisUtterance(chunks[idx])
      u.rate = speedRef.current
      if (voiceRef.current) {
        u.voice = voiceRef.current
        u.lang = voiceRef.current.lang
      } else {
        u.lang = "en-GB" // best-effort even without an exact voice match
      }

      // Watchdog: if Chrome accepts the utterance but never actually starts
      // producing audio, bail out of this chunk instead of hanging forever.
      let started = false
      const watchdog = setTimeout(() => {
        if (!started && !myCancelToken.cancelled) {
          idx++
          speakNext()
        }
      }, 2000)

      u.onstart = () => {
        started = true
        clearTimeout(watchdog)
        if (!firedStart) { firedStart = true; setActiveId(id) }
      }
      u.onend = () => {
        clearTimeout(watchdog)
        idx++
        speakNext()
      }
      u.onerror = () => {
        clearTimeout(watchdog)
        idx++
        speakNext()
      }

      window.speechSynthesis.speak(u)
    }

    speakNext()
  }, [])

  const speak = useCallback((id, text) => {
    if (!window.speechSynthesis) return
    if (activeIdRef.current === id) { stop(); return }
    activeTextRef.current = text
    _startUtterance(id, text)
  }, [stop, _startUtterance])

  const setSpeedIndex = useCallback((i) => {
    const clamped = Math.max(0, Math.min(SPEED_OPTIONS.length - 1, i))
    speedRef.current = SPEED_OPTIONS[clamped].rate
    setSpeedIndexRaw(clamped)
    if (activeIdRef.current && activeTextRef.current) {
      _startUtterance(activeIdRef.current, activeTextRef.current)
    }
  }, [_startUtterance])

  return {
    speak, stop, activeId,
    speedIndex, setSpeedIndex, speedOptions: SPEED_OPTIONS,
    voiceReady, voiceName,
  }
}

// ─── SpeedControl ────────────────────────────────────────────────────────────

export function SpeedControl({ speedIndex, setSpeedIndex, speedOptions }) {
  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      background: "#f3f4f6",
      borderRadius: "999px",
      padding: "3px 6px",
    }}>
      {speedOptions.map((opt, i) => {
        const active = i === speedIndex
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => setSpeedIndex(i)}
            style={{
              border: "none",
              borderRadius: "999px",
              padding: "2px 9px",
              fontSize: "12px",
              fontWeight: active ? "700" : "400",
              cursor: "pointer",
              background: active ? "#3b82f6" : "transparent",
              color: active ? "#ffffff" : "#374151",
              transition: "background 0.15s, color 0.15s",
              lineHeight: "1.6",
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// ─── SpeakerButton ───────────────────────────────────────────────────────────

export function SpeakerButton({
  id,
  html,
  text,
  speak,
  stop,
  activeId,
  style,
  className = "",
}) {
  const isActive = activeId === id

  const content = text ?? htmlToText(html)
  if (!content) return null

  const handleClick = () => {
    const c = text ?? htmlToText(html)
    if (!c) return
    speak(id, c)
  }

  return (
    <div
      className={`question-aloud-icon ${className}`}
      onClick={handleClick}
      title={isActive ? "Stop reading" : "Read aloud"}
      aria-label={isActive ? "Stop reading" : "Read aloud"}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") handleClick() }}
      style={{ cursor: "pointer", ...style }}
    >
      {isActive ? <StopIcon /> : <SpeakerIcon />}
    </div>
  )
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function SpeakerIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x={0} y={0}
      viewBox="0 0 24 24"
      style={{ enableBackground: "new 0 0 512 512" }}
      xmlSpace="preserve"
    >
      <g>
        <path
          d="M20.8 4.293a1 1 0 0 0-1.41 1.414 8.911 8.911 0 0 1 0 12.586 1 1 0 1 0 1.41 1.414 10.911 10.911 0 0 0 0-15.414Z"
          fill="currentcolor" opacity={1} data-original="currentcolor"
        />
        <path
          d="M18.093 7.293a1 1 0 1 0-1.414 1.414 4.664 4.664 0 0 1 0 6.586 1 1 0 1 0 1.414 1.414 6.665 6.665 0 0 0 0-9.414ZM13.819.207A12.055 12.055 0 0 0 6.268 5H5a5.006 5.006 0 0 0-5 5v4a5.006 5.006 0 0 0 5 5h1.269a12.051 12.051 0 0 0 7.55 4.793A1 1 0 0 0 15 22.81V1.19a1 1 0 0 0-1.181-.983Z"
          fill="currentcolor" opacity={1} data-original="currentcolor"
        />
      </g>
    </svg>
  )
}

function StopIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x={0} y={0}
      viewBox="0 0 24 24"
      style={{ enableBackground: "new 0 0 512 512" }}
      xmlSpace="preserve"
    >
      <g>
        <rect x="3.5" y="3.5" width="16" height="16" rx="2.5" ry="2.5" fill="#ffffff" />
      </g>
    </svg>
  )
}

// ─── ResultSpeakButton ────────────────────────────────────────────────────

export function ResultSpeakButton({
  id,
  parts,
  speak,
  activeId,
  resultData,
  className = "",
  style,
}) {
  const isActive = activeId === id

  const variant =
    resultData && parseInt(resultData.is_correct) === 1
      ? parseInt(resultData.attempCount) >= 2
        ? "warn"
        : "ok"
      : "err"

  const handleClick = () => {
    const text = (Array.isArray(parts) ? parts : [parts])
      .filter(Boolean)
      .join(". ")
    if (!text) return
    speak(id, htmlToText(text))
  }

  return (
    <button
      type="button"
      className={`nv-feedback-card__speak-btn nv-feedback-card__speak-btn--${variant} ${className}`}
      style={style}
      onClick={handleClick}
      aria-label={isActive ? "Stop reading" : "Read result aloud"}
      title={isActive ? "Stop reading" : "Read result aloud"}
    >
      {isActive ? <i className="mdi mdi-stop" /> : <i className="mdi mdi-volume-high" />}
    </button>
  )
}